import { flg } from "@aix/webutils/src/ui/logging"
import { useLocalStorage } from "@aix/ui"
import { useLocation } from "@tanstack/react-router"
import { useEffect, useState } from "react"

const MARKETING_QUERY_KEYS = [
  "fbclid",
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const

type MarketingQueryKey = (typeof MARKETING_QUERY_KEYS)[number]
type MarketingQuery = Partial<Record<MarketingQueryKey, string>>

export const extractMarketingQuery = (rawSearch: string): MarketingQuery => {
  const params = new URLSearchParams(rawSearch)
  return Object.fromEntries(
    MARKETING_QUERY_KEYS.flatMap((key) => {
      const value = params.get(key)
      return value ? [[key, value] as const] : []
    }),
  )
}

export const useGlobalQuery = () => {
  const location = useLocation()
  const [existingMarketingData, setMarketingData] = useLocalStorage("marketingData", {})
  const [marketingDataSent, setMarketingDataSent] = useLocalStorage("marketingDataSent", false)
  const [marketingDataFired, setMarketingDataFired] = useState(false)
  const [isLocalStorageLoaded, setIsLocalStorageLoaded] = useState(false)

  const getFacebookCookies = () => {
    const cookies = document.cookie.split("; ")
    const fbc = cookies.find((cookie) => cookie.startsWith("_fbc="))?.split("=")[1] || null
    const fbp = cookies.find((cookie) => cookie.startsWith("_fbp="))?.split("=")[1] || null

    return { fbc, fbp }
  }

  // Effect to detect when localStorage is loaded
  useEffect(() => {
    if (existingMarketingData !== undefined) {
      setIsLocalStorageLoaded(true)
    }
  }, [existingMarketingData])

  useEffect(() => {
    // Wait for localStorage to be loaded and prevent multiple fires
    if (!isLocalStorageLoaded || marketingDataFired) return

    const { fbclid, gclid, utm_source, utm_medium, utm_campaign, utm_content, utm_term } = extractMarketingQuery(
      window.location.search,
    )
    const { fbc, fbp } = getFacebookCookies()

    const newMarketingData = {
      gclid,
      fbc: fbclid || fbc || undefined,
      fbp: fbp || undefined,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      utm_term,
    }

    // Filter out undefined values from new data only
    const filteredMarketingData = Object.fromEntries(
      Object.entries(newMarketingData).filter(([_, value]) => value !== undefined),
    )

    // Only update if we have new data to merge
    if (Object.keys(filteredMarketingData).length > 0) {
      // Merge new data with existing data, prioritizing existing values
      const updatedMarketingData = { ...existingMarketingData, ...filteredMarketingData }
      flg.info("updatedMarketingData")
      setMarketingData(updatedMarketingData)
      setMarketingDataSent(false)
      setMarketingDataFired(true)
    }
  }, [location.href, marketingDataFired, isLocalStorageLoaded, existingMarketingData])
}
